import {
  addAddressError,
  removeAddressError,
  updateElectricityMeterAddressData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { subscriptionFormActionsV2 } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormV2Sagas'
import { useDispatch, useSelector } from 'react-redux'
import useElectricityMeter from '@/containers/SubscriptionFormPageNew/Components/ElectricityMeter/useElectricityMeter'
import {
  selectActiveAddress,
  selectCurrentAddressErrors,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import {
  ELECTRICITY_METER_ERROR_KEYS,
  ELECTRICITY_METER_ID_LENGTH,
  ELECTRICITY_METER_KEY,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Common/constants'
import { modulesConfig } from '@/containers/SubscriptionFormPageNew/config'

const useMeterInput = ({ meterKey = ELECTRICITY_METER_KEY } = {}) => {
  const dispatch = useDispatch()
  const { country } = useSelector(selectAuthData)
  const skipCheck = modulesConfig[country]?.meter?.skipSyntaxCheck ?? false
  const showMeterSerialNumber = modulesConfig[country]?.meter?.meterSerialNumber ?? false
  const { pdlNo, meterSerialNumber, isEditable, lastSentDate, isValidPdl, t, canRequestMeterData } =
    useElectricityMeter({
      type: 'client',
      meterKey,
    })
  const activeAddress = useSelector(selectActiveAddress)
  const errors = useSelector(selectCurrentAddressErrors)
  const status = isValidPdl ? 'valid' : 'error'
  const searchPdlAvailableCountries = ['fr']
  const showPdlSearchButton = searchPdlAvailableCountries.includes(country)

  const getMeterIdLabel = () => {
    const label = t('subscriptionForm:pdl_module.meter_id.label')
    return pdlNo ? label : ''
  }

  const meterIdMaxLength = showMeterSerialNumber ? ELECTRICITY_METER_ID_LENGTH[country]?.[0] : undefined

  const errorKey = ELECTRICITY_METER_ERROR_KEYS[meterKey]

  const electricityMeterUpdate = payload => {
    dispatch(
      updateElectricityMeterAddressData({
        data: payload,
        id: activeAddress,
        meterKey,
      }),
    )
  }

  const meterIdOnBlurHandler = value => {
    const newPdlNo = value?.replace(/ /g, '') ?? ''
    const currentPdlNo = pdlNo ?? ''

    // No-op blur: the field value hasn't changed. Avoid dispatching anything
    // — otherwise we'd reset enedisAuth/consentDate/index fields AND flip the
    // dirty flag on the global save warning, even though the user didn't
    // actually edit anything (e.g. blur on an empty field at form load).
    if (newPdlNo === currentPdlNo) {
      return
    }

    electricityMeterUpdate({
      pdlNo: newPdlNo,
      enedisAuth: false,
      consentDate: null,
      meterPower: null,
      meterOption: null,
      indexHC: null,
      indexBaseHP: null,
      readingDate: null,
    })

    if (!newPdlNo) {
      electricityMeterUpdate({ isValidPdl: false })
      dispatch(removeAddressError({ id: activeAddress, key: errorKey }))
      return
    }

    if (skipCheck) {
      setIsValidPdl(true)
      return
    }

    if (!ELECTRICITY_METER_ID_LENGTH[country]?.includes(newPdlNo.length)) {
      setIsValidPdl(false)
      return
    }

    dispatch({
      type: subscriptionFormActionsV2.METER_MODULE_VALIDATE_PDL_METER_SYNTAX_V2,
      meterId: newPdlNo,
      callback: setIsValidPdl,
    })
  }

  const setIsValidPdl = value => {
    electricityMeterUpdate({ isValidPdl: value })
    if (value === false && (!pdlNo || pdlNo?.length === 0)) {
      return
    }
    if (value === false && pdlNo?.length > 0) {
      dispatch(
        addAddressError({
          data: { [errorKey]: [t('subscriptionForm:pdl_module.error_validation.pdl_syntax')] },
          id: activeAddress,
        }),
      )
      return
    }

    dispatch(removeAddressError({ id: activeAddress, key: errorKey }))
  }

  const meterSerialOnBlurHandler = value => {
    const newMeterSerialNumber = value?.replace(/ /g, '') ?? ''
    const currentMeterSerialNumber = meterSerialNumber ?? ''

    if (newMeterSerialNumber === currentMeterSerialNumber) {
      return
    }

    electricityMeterUpdate({ meterSerialNumber: newMeterSerialNumber })
  }

  return {
    meterIdOnBlurHandler,
    meterSerialOnBlurHandler,
    pdlNo,
    meterSerialNumber,
    showMeterSerialNumber,
    meterIdLabel: getMeterIdLabel(),
    isEditable,
    errors,
    lastSentDate,
    canRequestMeterData,
    meterStatus: pdlNo ? status : null,
    showPdlSearchButton,
    meterIdMaxLength,
    errorKey,
    t,
  }
}

export default useMeterInput
